import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { StateApiError, checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError } from '@trovata/app/core/models/state-error.model';
import { StablecoinWorkflowsHttpService } from '../../../services/workflows/workflows-http.service';
import { StablecoinWorkflow } from '../../../models/workflow.model';
import {
	ArchiveStablecoinWorkflow,
	ClearStablecoinWorkflowsState,
	CreateStablecoinWorkflow,
	DecideStablecoinWorkflow,
	InitStablecoinWorkflowsState,
	LoadStablecoinWorkflows,
	ResetStablecoinWorkflowsState,
	UpdateStablecoinWorkflow,
} from '../../actions/workflows.actions';

export interface StablecoinWorkflowsStateModel {
	workflows: StablecoinWorkflow[] | null;
	apiErrors: StateApiError[];
	isCached: boolean;
	workflowsLoading: boolean;
}

const DEFAULT_STABLECOIN_WORKFLOWS_STATE: StablecoinWorkflowsStateModel = {
	workflows: null,
	apiErrors: [],
	isCached: false,
	workflowsLoading: true,
};

@State<StablecoinWorkflowsStateModel>({
	name: 'stablecoinWorkflows',
	defaults: { ...DEFAULT_STABLECOIN_WORKFLOWS_STATE },
})
@Injectable()
export class StablecoinWorkflowsState {
	private readonly workflowsService: StablecoinWorkflowsHttpService = inject(StablecoinWorkflowsHttpService);

	@Selector()
	static workflows(state: StablecoinWorkflowsStateModel): StablecoinWorkflow[] | null {
		return state.workflows;
	}

	@Selector()
	static workflowsLoading(state: StablecoinWorkflowsStateModel): boolean {
		return state.workflowsLoading;
	}

	@Selector()
	static apiErrors(state: StablecoinWorkflowsStateModel): StateApiError[] {
		return state.apiErrors;
	}

	@Action(InitStablecoinWorkflowsState)
	init(): void {
		// Fetches are dispatched lazily from page components (no entitlement gate yet).
	}

	@Action(ClearStablecoinWorkflowsState)
	clear(context: StateContext<StablecoinWorkflowsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_WORKFLOWS_STATE });
	}

	@Action(ResetStablecoinWorkflowsState)
	reset(context: StateContext<StablecoinWorkflowsStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_WORKFLOWS_STATE });
	}

	@Action(LoadStablecoinWorkflows)
	loadWorkflows(context: StateContext<StablecoinWorkflowsStateModel>): Observable<StablecoinWorkflow[]> {
		context.patchState({ workflowsLoading: true });
		return this.workflowsService.getWorkflows().pipe(
			tap((workflows: StablecoinWorkflow[]) => {
				checkToRemoveStateApiError(context, LoadStablecoinWorkflows);
				context.patchState({ workflows, workflowsLoading: false, isCached: true });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ workflowsLoading: false });
				checkToAddStateApiError(error, context, LoadStablecoinWorkflows, checkForBadHttpErrorMessage(error, 'Unable to load workflows.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinWorkflow)
	createWorkflow(context: StateContext<StablecoinWorkflowsStateModel>, action: CreateStablecoinWorkflow): Observable<StablecoinWorkflow> {
		return this.workflowsService.createWorkflow(action.body).pipe(
			tap((workflow: StablecoinWorkflow) => {
				checkToRemoveStateApiError(context, CreateStablecoinWorkflow);
				const state: StablecoinWorkflowsStateModel = context.getState();
				context.patchState({ workflows: [workflow, ...(state.workflows ?? [])], isCached: true });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, CreateStablecoinWorkflow, checkForBadHttpErrorMessage(error, 'Unable to create workflow.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(UpdateStablecoinWorkflow)
	updateWorkflow(context: StateContext<StablecoinWorkflowsStateModel>, action: UpdateStablecoinWorkflow): Observable<StablecoinWorkflow> {
		return this.workflowsService.updateWorkflow(action.workflowId, action.body).pipe(
			tap((workflow: StablecoinWorkflow) => {
				checkToRemoveStateApiError(context, UpdateStablecoinWorkflow);
				// Editing a live workflow returns a NEW pending version — prepend it; in-place edits replace their row.
				const state: StablecoinWorkflowsStateModel = context.getState();
				const exists: boolean = !!state.workflows?.some((item: StablecoinWorkflow) => item.id === workflow.id);
				if (exists) {
					this.replaceWorkflow(context, workflow);
				} else {
					context.patchState({ workflows: [workflow, ...(state.workflows ?? [])], isCached: true });
				}
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, UpdateStablecoinWorkflow, checkForBadHttpErrorMessage(error, 'Unable to update workflow.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(DecideStablecoinWorkflow)
	decideWorkflow(context: StateContext<StablecoinWorkflowsStateModel>, action: DecideStablecoinWorkflow): Observable<StablecoinWorkflow> {
		return this.workflowsService.decideWorkflow(action.workflowId, action.decision, action.comment).pipe(
			tap((workflow: StablecoinWorkflow) => {
				checkToRemoveStateApiError(context, DecideStablecoinWorkflow);
				this.replaceWorkflow(context, workflow);
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, DecideStablecoinWorkflow, checkForBadHttpErrorMessage(error, 'Unable to record decision.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(ArchiveStablecoinWorkflow)
	archiveWorkflow(context: StateContext<StablecoinWorkflowsStateModel>, action: ArchiveStablecoinWorkflow): Observable<StablecoinWorkflow> {
		return this.workflowsService.archiveWorkflow(action.workflowId).pipe(
			tap((workflow: StablecoinWorkflow) => {
				checkToRemoveStateApiError(context, ArchiveStablecoinWorkflow);
				this.replaceWorkflow(context, workflow);
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, ArchiveStablecoinWorkflow, checkForBadHttpErrorMessage(error, 'Unable to archive workflow.').message);
				return throwError(() => error);
			})
		);
	}

	private replaceWorkflow(context: StateContext<StablecoinWorkflowsStateModel>, workflow: StablecoinWorkflow): void {
		const state: StablecoinWorkflowsStateModel = context.getState();
		context.patchState({
			workflows: state.workflows?.map((item: StablecoinWorkflow) => (item.id === workflow.id ? workflow : item)) ?? null,
		});
	}
}
